import React from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { Box, Stack } from '@mui/material';
import { object, boolean } from 'yup';
import { useParams } from 'react-router-dom';
import { isPIOptions, IS_PI_TYPE } from './options';
import { type DsrcDeclaration } from './types';
import { PAGE_TEXT } from '../../../../lang';
import {
  TextField,
  Checkbox,
  Button,
  Select,
  UserRole,
} from '../../../../../../components';
import {
  FundingSource,
  ReturnedRemarksDisplay,
} from '../../../../../Nomination/components';
import { type ApplicationRemarkType } from '../../../../../Nomination/graphql';
import { theme } from '../../../../../../Theme';
import { handleReadySubmit } from '../../../../../../utils';
import {
  MONITOR_SIDEBAR_MODULE,
  NOMINATION_TEXT,
} from '../../../../../Monitor/components';

type DeclarationAssessmentsboxProps = {
  initialValues: DsrcDeclaration;
  isViewMode?: boolean;
  recommendForHKPFS: boolean;
  stipendBudgetRequired: boolean;
  returnedRemarks: ApplicationRemarkType[];
  fundingSchema?: string;
  fundingSource?: string;
  onSubmit: (values: any) => void;
};

export const DeclarationAssessmentsbox: React.FunctionComponent<
  DeclarationAssessmentsboxProps
> = props => {
  const { DRC_ASSESSMENT_DETAILS } = PAGE_TEXT;
  const {
    initialValues,
    isViewMode,
    recommendForHKPFS,
    stipendBudgetRequired,
    fundingSource,
    returnedRemarks,
    onSubmit,
  } = props;
  const { applicationId = '' } = useParams();

  const targetRemarks = (
    fromRole: UserRole,
    toRole: UserRole,
  ): ApplicationRemarkType[] => {
    return returnedRemarks.filter(
      rmk => rmk.fromRole === fromRole && rmk.toRole.includes(toRole),
    );
  };

  const remarksFromDrc = targetRemarks(UserRole.DRC, UserRole.DO_RPG_S);
  const remarksFromHod = targetRemarks(UserRole.HOD_DOS, UserRole.DO_RPG_S);
  const remarksFromFrc = targetRemarks(UserRole.FRC, UserRole.DO_RPG_S);
  const remarksFromGs = targetRemarks(UserRole.GS, UserRole.DO_RPG_S);
  const remarksToSup = targetRemarks(UserRole.DO_RPG_S, UserRole.SUPERVISOR);
  const remarksToApp = targetRemarks(UserRole.DO_RPG_S, UserRole.APPLICANT);

  const hasReturnHistory =
    Boolean(remarksFromDrc.length) ||
    Boolean(remarksFromFrc.length) ||
    Boolean(remarksFromGs.length) ||
    Boolean(remarksToSup.length) ||
    Boolean(remarksFromHod.length) ||
    Boolean(remarksToApp.length);

  const STEP_TEXT = DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_H;

  const remarksMap = [
    { title: STEP_TEXT.RETURN_REMARKS.GS, remarkArr: remarksFromGs },
    { title: STEP_TEXT.RETURN_REMARKS.FRC, remarkArr: remarksFromFrc },
    { title: STEP_TEXT.RETURN_REMARKS.HOD, remarkArr: remarksFromHod },
    { title: STEP_TEXT.RETURN_REMARKS.DRC, remarkArr: remarksFromDrc },
    { title: STEP_TEXT.RETURN_REMARKS.SUP, remarkArr: remarksToSup },
    { title: STEP_TEXT.RETURN_REMARKS.APP, remarkArr: remarksToApp },
  ];

  const isGRF =
    fundingSource === FundingSource.INCENTIVE_GRF_ECS_3_PLUS_1_MATCHING_FUND;
  const isStrategicGlobal =
    fundingSource === FundingSource.STRATEGIC_HIRING_SCHEME ||
    fundingSource === FundingSource.GLOBAL_STEM_PROFESSORSHIP_SCHEME;

  const handlePIofGRFChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ): void => {
    const { value } = event.target;
    void formik.setFieldValue('PIofGRF', value === IS_PI_TYPE.YES);
  };

  const handlePIofStemChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ): void => {
    const { value } = event.target;
    void formik.setFieldValue('PIofStem', value === IS_PI_TYPE.YES);
  };

  const formik = useFormik<DsrcDeclaration>({
    initialValues,
    validationSchema: object().shape({
      PIofStem: boolean().when([], ([_], schema) => {
        return isStrategicGlobal
          ? schema.required('Required')
          : schema.notRequired();
      }),
      PIofGRF: boolean().when([], ([_], schema) => {
        return isGRF ? schema.required('Required') : schema.notRequired();
      }),
      proof: boolean().required().oneOf([true]),
      recommendThreshold: boolean().when([], ([_], schema) => {
        return recommendForHKPFS
          ? schema.required().oneOf([true])
          : schema.notRequired();
      }),
      stipendBudgetReserved: boolean().when([], ([_], schema) => {
        return stipendBudgetRequired
          ? schema.required().oneOf([true])
          : schema.notRequired();
      }),
    }),
    onSubmit,
  });

  const isPIofStemValue = formik.values.PIofStem
    ? IS_PI_TYPE.YES
    : IS_PI_TYPE.NO;

  const isPIofGRFValue =
    typeof formik.values.PIofGRF !== 'boolean'
      ? ''
      : formik.values.PIofGRF
        ? IS_PI_TYPE.YES
        : IS_PI_TYPE.NO;

  const generateHyperlink = (applicationId: string): React.ReactNode => {
    return (
      <>
        &nbsp;(Click&nbsp;
        <a
          href={`/internal/monitor/${applicationId}?am=${MONITOR_SIDEBAR_MODULE.SUPERVISOR_NOMINATION}&lst=${NOMINATION_TEXT.FUNDING_SOURCE.TITLE}`}
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>
        &nbsp;to view the account(s).)
      </>
    );
  };

  return (
    <ContentBox>
      <Stack paddingX={2}>
        <form onSubmit={handleReadySubmit(formik)} noValidate>
          <Box padding={2}>
            <h2 className="font-bold mb-4">{STEP_TEXT.BY_SUBMITTING}</h2>
            {isStrategicGlobal && (
              <div className="PI-select-area">
                <span className="mr-4">{STEP_TEXT.PI_OF_STEM}</span>
                <Select
                  id="PIofStem"
                  name="PIofStem"
                  className="w-1/3"
                  options={isPIOptions}
                  InputLabelProps={{ shrink: true }}
                  onChange={handlePIofStemChange}
                  onBlur={formik.handleBlur}
                  value={isPIofStemValue}
                  error={
                    Boolean(formik.touched.PIofStem) &&
                    Boolean(formik.errors.PIofStem)
                  }
                  helperText={formik.touched.PIofStem && formik.errors.PIofStem}
                />
              </div>
            )}

            {isGRF && (
              <div className="PI-select-area">
                <span className="mr-4">{STEP_TEXT.PI_OF_GRF}</span>
                <Select
                  id="PIofGRF"
                  name="PIofGRF"
                  className="w-1/3"
                  options={isPIOptions}
                  InputLabelProps={{ shrink: true }}
                  onChange={handlePIofGRFChange}
                  onBlur={formik.handleBlur}
                  value={isPIofGRFValue}
                  error={
                    Boolean(formik.touched.PIofGRF) &&
                    Boolean(formik.errors.PIofGRF)
                  }
                  helperText={formik.touched.PIofGRF && formik.errors.PIofGRF}
                />
              </div>
            )}

            <div className="pl-4">
              <Checkbox
                name={'proof'}
                disabled={isViewMode}
                label={STEP_TEXT.IS_PROOF}
                value={formik.values.proof}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.touched?.proof) &&
                  Boolean(formik.errors?.proof)
                }
              />
            </div>

            {recommendForHKPFS && (
              <div className="pl-4">
                <Checkbox
                  sx={{
                    alignItems: 'flex-start',
                  }}
                  name={'recommendThreshold'}
                  disabled={isViewMode}
                  error={
                    Boolean(formik.touched?.recommendThreshold) &&
                    Boolean(formik.errors?.recommendThreshold)
                  }
                  label={
                    <div className="mt-2">
                      {STEP_TEXT.HKPFS_RECOMMENDATION.MEET_F}
                      <a
                        href={STEP_TEXT.HKPFS_RECOMMENDATION.LINK.URL}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {STEP_TEXT.HKPFS_RECOMMENDATION.LINK.LABEL}
                      </a>
                      {STEP_TEXT.HKPFS_RECOMMENDATION.MEET_L}
                    </div>
                  }
                  value={formik.values.recommendThreshold}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            )}

            {stipendBudgetRequired && (
              <div className="pl-4">
                <Checkbox
                  sx={{
                    alignItems: 'flex-start',
                  }}
                  name={'stipendBudgetReserved'}
                  disabled={isViewMode}
                  error={
                    Boolean(formik.touched?.stipendBudgetReserved) &&
                    Boolean(formik.errors?.stipendBudgetReserved)
                  }
                  label={
                    <div className="mt-2">
                      {STEP_TEXT.MAKE_UP_STIPEND_BUDGET}
                      {generateHyperlink(applicationId)}
                    </div>
                  }
                  value={formik.values.stipendBudgetReserved}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            )}
          </Box>
          <Stack paddingTop={4}>
            {hasReturnHistory && (
              <Box padding={2} bgcolor={theme.palette.grey['200']}>
                {remarksMap.map((item, index) => {
                  const { title, remarkArr } = item;
                  return (
                    <div key={index}>
                      {ReturnedRemarksDisplay(title, remarkArr)}
                    </div>
                  );
                })}
              </Box>
            )}

            <Box padding={2}>
              <h3 className="font-bold mb-4">{STEP_TEXT.REMARKS}</h3>
              <TextField
                className="w-full"
                disabled={isViewMode}
                InputLabelProps={{ shrink: true }}
                multiline
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                minRows={4}
              />
            </Box>
          </Stack>
          {!isViewMode && (
            <div className="save-area">
              <Button variant="contained" color="primary" type="submit">
                {PAGE_TEXT.SAVE}
              </Button>
            </div>
          )}
        </form>
      </Stack>
    </ContentBox>
  );
};
const ContentBox = styled.div`
  flex-direction: column;
  align-items: center;
  flex: 1;

  .save-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 4rem;
    margin-bottom: 1rem;
  }

  .PI-select-area {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 1rem;
  }
`;
